import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// About Us API Service (Single Type)
export const aboutUsService = {
  // Get about us page data (Single Type - returns single object)
  async getAboutUsData(params = {}) {
    try {
      // Use proper population for gallery components
      const response = await api.get('/about-us?populate[clientSection][populate]=*&populate[iamges][populate]=*&populate[awardImage][populate]=*');
      // For Single Types, the data is directly in response.data.data
      return {
        data: response.data.data,
        meta: response.data.meta,
        success: true
      };
    } catch (error) {
      console.error('Error fetching about us data:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      
      return {
        data: null,
        success: false,
        error: error.response?.data?.error?.message || error.message || 'Failed to fetch about us data'
      };
    }
  }
};

// Helper function to format image URLs (same as contactService)
export const formatImageUrl = (imageData) => {
  if (!imageData) return null;
  
  // Use base Strapi URL (without /api) for images
  const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:1337';
  
  if (typeof imageData === 'object' && imageData.url) {
    return imageData.url.startsWith('http') ? imageData.url : `${baseUrl}${imageData.url}`;
  }
  
  return null;
};

// Helper function to format about us data
export const formatAboutUsData = (aboutUsData) => {
  if (!aboutUsData) return null;

  return {
    ...aboutUsData,
    // Format main images from gallery components - Updated for new structure
    images: aboutUsData.iamges?.map((galleryItem, index) => {
      // Handle new structure where images is an array within gallery component
      let imageUrl = null;
      let altText = `Main Image ${index + 1}`;
      
      if (galleryItem.images && Array.isArray(galleryItem.images) && galleryItem.images.length > 0) {
        // Use the first image from the array
        const firstImage = galleryItem.images[0];
        imageUrl = formatImageUrl(firstImage);
        altText = firstImage.alternativeText || altText;
      } else if (galleryItem.images && !Array.isArray(galleryItem.images)) {
        // Backward compatibility for single image
        imageUrl = formatImageUrl(galleryItem.images);
        altText = galleryItem.images.alternativeText || altText;
      }

      return {
        ...galleryItem,
        url: imageUrl,
        alt: altText,
      };
    }) || [],
    // Format icon images (direct media field)
    iconImages: aboutUsData.iconImages?.map(image => ({
      ...image,
      url: formatImageUrl(image),
    })) || [],
    // Format award images from gallery components - Updated for new structure
    awardImage: aboutUsData.awardImage?.map((galleryItem, index) => {
      // Handle new structure where images is an array within gallery component
      let imageUrl = null;
      let altText = `Award Image ${index + 1}`;
      
      if (galleryItem.images && Array.isArray(galleryItem.images) && galleryItem.images.length > 0) {
        // Use the first image from the array
        const firstImage = galleryItem.images[0];
        imageUrl = formatImageUrl(firstImage);
        altText = firstImage.alternativeText || altText;
      } else if (galleryItem.images && !Array.isArray(galleryItem.images)) {
        // Backward compatibility for single image
        imageUrl = formatImageUrl(galleryItem.images);
        altText = galleryItem.images.alternativeText || altText;
      }

      return {
        ...galleryItem,
        url: imageUrl,
        alt: altText,
      };
    }) || [],
    // Format leader profile images (should now be properly populated)
    leaderProfile: aboutUsData.leaderProfile?.map(leader => ({
      ...leader,
      // Handle both single and multiple images
      image: leader.image && leader.image.length > 0 ? {
        ...leader.image[0],
        url: formatImageUrl(leader.image[0]),
      } : (leader.image ? {
        ...leader.image,
        url: formatImageUrl(leader.image),
      } : null),
    })) || [],
    // Format we believe section with nested items (should now be properly populated)
    weBelieveSection: aboutUsData.weBelieveSection ? {
      // Use header data for main section info
      title: aboutUsData.weBelieveSection.weBelieveHeader?.title,
      description: aboutUsData.weBelieveSection.weBelieveHeader?.description,
      // Transform weBelieveItem array to flat images array and structured data
      images: aboutUsData.weBelieveSection.weBelieveItem?.map(item => ({
        url: formatImageUrl(item.image),
        alternativeText: item.image?.alternativeText || item.title,
      })) || [],
      // Pass all weBelieveItem dynamically instead of hardcoding first 3
      weBelieveItem: aboutUsData.weBelieveSection.weBelieveItem?.map(item => ({
        title: item.title,
        description: item.description,
        image: item.image ? {
          url: formatImageUrl(item.image),
          alternativeText: item.image?.alternativeText || item.title,
        } : null,
      })) || [],
      // Keep old structure for backward compatibility
      coreValues: aboutUsData.weBelieveSection.weBelieveItem?.[0] ? {
        title: aboutUsData.weBelieveSection.weBelieveItem[0].title,
        description: aboutUsData.weBelieveSection.weBelieveItem[0].description,
        points: aboutUsData.weBelieveSection.weBelieveItem[0].description?.split('\n').filter(line => line.trim()) || [],
      } : null,
      vision: aboutUsData.weBelieveSection.weBelieveItem?.[1] ? {
        title: aboutUsData.weBelieveSection.weBelieveItem[1].title,
        description: aboutUsData.weBelieveSection.weBelieveItem[1].description,
      } : null,
      mission: aboutUsData.weBelieveSection.weBelieveItem?.[2] ? {
        title: aboutUsData.weBelieveSection.weBelieveItem[2].title?.split('\n')[0],
        subtitle: aboutUsData.weBelieveSection.weBelieveItem[2].description?.split('\n')[0],
        description: aboutUsData.weBelieveSection.weBelieveItem[2].description?.split('\n').slice(2).join('\n'),
      } : null,
    } : null,
    // Format cards items images - Updated for new structure
    cardsItems: aboutUsData.cardsItems?.map(card => {
      // Handle new structure for card images
      let cardImageUrl = null;
      if (card.images && Array.isArray(card.images) && card.images.length > 0) {
        // Use the first image from the array
        cardImageUrl = formatImageUrl(card.images[0]);
      } else if (card.images && !Array.isArray(card.images)) {
        // Backward compatibility for single image
        cardImageUrl = formatImageUrl(card.images);
      }

      return {
        ...card,
        images: cardImageUrl ? {
          url: cardImageUrl,
        } : null,
      };
    }) || [],
    // Format client section data - Updated for new gallery structure
    clientSection: aboutUsData.clientSection ? {
      ...aboutUsData.clientSection,
      clients: aboutUsData.clientSection.clientImages?.map((clientItem, index) => {
        // Handle new structure where images is an array within gallery component
        let logoUrl = null;
        if (clientItem.images && Array.isArray(clientItem.images) && clientItem.images.length > 0) {
          // Use the first image from the array
          logoUrl = formatImageUrl(clientItem.images[0]);
        } else if (clientItem.images && !Array.isArray(clientItem.images)) {
          // Backward compatibility for single image
          logoUrl = formatImageUrl(clientItem.images);
        }

        return {
          id: index + 1,
          name: clientItem.title || `Client ${index + 1}`,
          subtitle: clientItem.description || '',
          logo: logoUrl,
        };
      }) || [],
    } : null,
    
    // Format features component - handle object structure with numeric keys
    features: aboutUsData.features ? (() => {
      // Handle case where features is an object with numeric keys (Strapi component structure)
      let featuresData = aboutUsData.features;
      if (featuresData["0"]) {
        featuresData = featuresData["0"];
      }
      
      return {
        ...featuresData,
        title: featuresData.title || 'Features',
        description: featuresData.description || '',
        objectiveItems: featuresData.objectiveItems?.map((item, itemIndex) => ({
          ...item,
          title: item.title || `Objective ${itemIndex + 1}`,
          description: item.description || ''
        })) || []
      };
    })() : null,
    
    // Format SystemActivation component
    SystemActivation: aboutUsData.SystemActivation ? {
      ...aboutUsData.SystemActivation,
      title: aboutUsData.SystemActivation.title || 'System Activation',
      subtitle: aboutUsData.SystemActivation.subtitle || '',
      description: aboutUsData.SystemActivation.description || ''
    } : null,
    
    // Format StrategicImplementation component - handle object structure with numeric keys
    StrategicImplementation: aboutUsData.StrategicImplementation ? (() => {
      // Handle case where StrategicImplementation is an object with numeric keys (Strapi component structure)
      let strategicData = aboutUsData.StrategicImplementation;
      if (strategicData["0"]) {
        strategicData = strategicData["0"];
      }
      
      return {
        ...strategicData,
        title: strategicData.title || 'Strategic Implementation',
        description: strategicData.description || '',
        objectiveItems: strategicData.objectiveItems?.map((item, itemIndex) => ({
          ...item,
          title: item.title || `Strategy ${itemIndex + 1}`,
          description: item.description || ''
        })) || []
      };
    })() : null,
    
    // Format TrustedPartner component - handle object structure with numeric keys
    TrustedPartner: aboutUsData.TrustedPartner ? (() => {
      // Handle case where TrustedPartner is an object with numeric keys (Strapi component structure)
      let trustedData = aboutUsData.TrustedPartner;
      if (trustedData["0"]) {
        trustedData = trustedData["0"];
      }
      
      return {
        ...trustedData,
        title: trustedData.title || 'Trusted Partner',
        description: trustedData.description || '',
        objectiveItems: trustedData.objectiveItems?.map((item, itemIndex) => ({
          ...item,
          title: item.title || `Trust Factor ${itemIndex + 1}`,
          description: item.description || ''
        })) || []
      };
    })() : null,
    
    // Format FeaturesOfTraining component - handle object structure with numeric keys
    FeaturesOfTraining: aboutUsData.FeaturesOfTraining ? (() => {
      // Handle case where FeaturesOfTraining is an object with numeric keys (Strapi component structure)
      let trainingData = aboutUsData.FeaturesOfTraining;
      if (trainingData["0"]) {
        trainingData = trainingData["0"];
      }
      
      return {
        ...trainingData,
        title: trainingData.title || 'Features of Training',
        description: trainingData.description || '',
        objectiveItems: trainingData.objectiveItems?.map((item, itemIndex) => ({
          ...item,
          title: item.title || `Training Feature ${itemIndex + 1}`,
          description: item.description || ''
        })) || []
      };
    })() : null,
  };
};

export default aboutUsService;
